import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Car, Plus, ArrowDownUp } from "lucide-react";
import { api } from "../api/client";
import { PageHeader } from "../components/Layout";
import { VehicleCard, VehicleCardSkeleton } from "../components/VehicleCard";
import { EmptyState, ErrorState } from "../components/EmptyState";
import { Button } from "../components/UIKit";
import { mapNetworkError } from "../lib/fleet";

const SORT_KEY = "myfleet_vehicles_sort";
const SORT_OPTIONS = [
  { value: "priority", label: "Priorité" },
  { value: "recent", label: "Récent" },
  { value: "brand", label: "Marque" },
  { value: "mileage", label: "Kilométrage" },
];

function loadInitialSort() {
  try {
    const v = localStorage.getItem(SORT_KEY);
    if (v && SORT_OPTIONS.some((o) => o.value === v)) return v;
  } catch { /* localStorage unavailable */ }
  return "priority";
}

export default function Vehicles() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [sort, setSort] = useState(loadInitialSort);

  const load = useCallback(async () => {
    try {
      // Always fetch with server-side priority sort; client applies its own
      // ordering for the remaining choices without any extra round-trip.
      const { data } = await api.get("/vehicles?sort=priority");
      setVehicles(data);
      setErrorMsg(null);
      setLoaded(true);
    } catch (e) {
      setErrorMsg(mapNetworkError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    try { localStorage.setItem(SORT_KEY, sort); } catch { /* ignore */ }
  }, [sort]);

  const sorted = useMemo(() => {
    const arr = [...vehicles];
    if (sort === "priority") return arr;
    if (sort === "recent") {
      return arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    if (sort === "brand") {
      return arr.sort((a, b) => `${a.brand} ${a.model}`.localeCompare(`${b.brand} ${b.model}`, "fr"));
    }
    if (sort === "mileage") {
      return arr.sort((a, b) => (b.mileage || 0) - (a.mileage || 0));
    }
    return arr;
  }, [vehicles, sort]);

  const isEmpty = !loading && loaded && vehicles.length === 0;
  const showError = !loading && !loaded && errorMsg;

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", position: "relative", minHeight: "100vh" }}>
      <PageHeader
        title="Véhicules"
        right={
          !loading && vehicles.length > 0 ? (
            <Button
              testID="vehicles-header-add"
              icon={<Plus size={18} />}
              onClick={() => navigate("/vehicles/new")}
              fullWidth={false}
              style={{ padding: "10px 18px" }}
            >
              Ajouter
            </Button>
          ) : null
        }
      />

      <div style={{ padding: "0 20px 100px" }}>
        {!loading && vehicles.length > 0 && (
          <SortBar sort={sort} onChange={setSort} />
        )}

        {loading ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
            <VehicleCardSkeleton />
            <VehicleCardSkeleton />
          </div>
        ) : showError ? (
          <ErrorState message={errorMsg} onRetry={() => { setLoading(true); load(); }} testID="vehicles-error" />
        ) : isEmpty ? (
          <EmptyState
            testID="vehicles-empty"
            icon={<Car size={64} color="var(--mf-border)" />}
            title="Aucun véhicule"
            subtitle="Ajoutez votre premier véhicule pour suivre entretiens, documents et alertes."
            action={
              <Button
                testID="vehicles-empty-add"
                icon={<Plus size={18} />}
                onClick={() => navigate("/vehicles/new")}
                fullWidth={false}
                style={{ paddingLeft: 22, paddingRight: 22 }}
              >
                Ajouter un véhicule
              </Button>
            }
          />
        ) : (
          <div
            data-testid="vehicles-grid"
            style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}
          >
            {sorted.map((v) => (
              <VehicleCard key={v.id} vehicle={v} testID={`vehicles-item-${v.id}`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SortBar({ sort, onChange }) {
  return (
    <div
      data-testid="vehicles-sort-bar"
      style={{
        display: "flex", alignItems: "center", gap: 8, marginBottom: 16,
        overflowX: "auto", paddingBottom: 4,
      }}
    >
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        fontSize: 12, fontWeight: 800,
        color: "var(--mf-text-secondary)", textTransform: "uppercase", letterSpacing: 0.5,
        flexShrink: 0,
      }}>
        <ArrowDownUp size={13} />
        Trier
      </span>
      {SORT_OPTIONS.map((opt) => {
        const active = opt.value === sort;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            data-testid={`vehicles-sort-${opt.value}`}
            style={{
              padding: "6px 14px", borderRadius: 999,
              background: active ? "var(--mf-brand)" : "var(--mf-bg-card)",
              color: active ? "#fff" : "var(--mf-text)",
              border: active ? "none" : "1px solid var(--mf-border)",
              fontWeight: 700, fontSize: 12, cursor: "pointer",
              whiteSpace: "nowrap", flexShrink: 0,
              transition: "background 150ms",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
